import type { PlanFeature, PlanTier, StakingTierData } from "./types";

// ── Legacy tiers (0-4) ────────────────────────────────────────────────────────
export const legacyPlanTiers: { [tier: number]: string } = {
  1: "Basic",
  2: "Growth",
  3: "Professional",
  4: "Enterprise",
};

export const isLegacyTier = (tier?: number) => !!tier && tier >= 1 && tier <= 4;

// ── New paid plan tiers (monthly 5-8, annual 9-12) ────────────────────────────
export const planTiers: PlanTier[] = [
  {
    title: "Builder",
    monthlyTier: 5,
    annualTier: 9,
    annualPrice: 24,
    monthlyPrice: 30,
    annualBilled: 288,
    description: "Perfect for startups or personal projects. Hardened and production-ready.",
    features: [
      { text: "50,000 credits/month*", included: true },
      // TODO(overage-credits): restore — see projects/pro/docs/overage-credits-scope.md
      // { text: "Overage credits: $0.00065**", included: true },
      { text: "30 requests/minute", included: true },
      { text: "All standard endpoints", included: true },
      { text: "AI Agent-friendly endpoints", included: true },
      { text: "Full web-socket access", included: false },
      { text: "MCP server and agent skills", included: false },
      { text: "Personal/non-profit license", included: true },
    ] as PlanFeature[],
  },
  {
    title: "Growth",
    monthlyTier: 6,
    annualTier: 10,
    annualPrice: 64,
    monthlyPrice: 80,
    annualBilled: 768,
    recommended: true,
    description: "Increased throughput and cutting-edge tools designed for growing businesses.",
    features: [
      { text: "450,000 credits/month*", included: true },
      // TODO(overage-credits): restore — see projects/pro/docs/overage-credits-scope.md
      // { text: "Overage credits: $0.00035**", included: true },
      { text: "90 requests/minute", included: true },
      { text: "All standard endpoints", included: true },
      { text: "AI Agent-friendly endpoints", included: true },
      { text: "Full web-socket access", included: true },
      { text: "MCP server and agent skills", included: true },
      { text: "Full commercial-use license", included: true },
    ] as PlanFeature[],
  },
  {
    title: "Professional",
    monthlyTier: 7,
    annualTier: 11,
    annualPrice: 180,
    monthlyPrice: 225,
    annualBilled: 2160,
    description: "Top-tier production-level access for businesses that require significant data.",
    features: [
      { text: "1,500,000 credits/month*", included: true },
      // TODO(overage-credits): restore — see projects/pro/docs/overage-credits-scope.md
      // { text: "Overage credits: $0.00025**", included: true },
      { text: "300 requests/minute", included: true },
      { text: "All standard endpoints", included: true },
      { text: "AI Agent-friendly endpoints", included: true },
      { text: "Full web-socket access", included: true },
      { text: "MCP server and agent skills", included: true },
      { text: "Full commercial-use license", included: true },
    ] as PlanFeature[],
  },
  {
    title: "Enterprise",
    monthlyTier: 8,
    annualTier: 12,
    annualPrice: 400,
    monthlyPrice: 500,
    annualBilled: 4800,
    description: "Open access to all the tools, data, and features CoinCap has to offer.",
    features: [
      { text: "5,000,000 credits/month*", included: true },
      // TODO(overage-credits): restore — see projects/pro/docs/overage-credits-scope.md
      // { text: "Overage credits: $0.00015**", included: true },
      { text: "900 requests/minute", included: true },
      { text: "All standard endpoints", included: true },
      { text: "AI Agent-friendly endpoints", included: true },
      { text: "Full web-socket access", included: true },
      { text: "MCP server and agent skills", included: true },
      { text: "Full commercial-use license", included: true },
    ] as PlanFeature[],
  },
];

// ── Fallback staking tiers ────────────────────────────────────────────────────
export const defaultStakingTiers: StakingTierData[] = [
  { tier: 5, name: "Builder", minStakedBalance: 45, credits: 50000, callsPerMinute: 30, ccPointsPerStakedMorPerDay: 2, hasWebSocket: false },
  { tier: 6, name: "Growth", minStakedBalance: 120, credits: 450000, callsPerMinute: 90, ccPointsPerStakedMorPerDay: 10, hasWebSocket: true },
  { tier: 7, name: "Professional", minStakedBalance: 338, credits: 1500000, callsPerMinute: 300, ccPointsPerStakedMorPerDay: 15, hasWebSocket: true },
  { tier: 8, name: "Enterprise", minStakedBalance: 750, credits: 5000000, callsPerMinute: 900, ccPointsPerStakedMorPerDay: 25, hasWebSocket: true },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
export const getPlanForTier = (tier?: number): string => {
  if (!tier || tier === 0) return "Free";
  if (isLegacyTier(tier)) return `${legacyPlanTiers[tier]} (Legacy)`;
  const plan = planTiers.find((p) => p.monthlyTier === tier || p.annualTier === tier);
  return plan?.title || "Unknown";
};
